/*
 * AdminDashboard — verqo Platform Command Center.
 *
 * The single landing page admins see at /admin. Aggregates every signal
 * that matters today across all portals:
 *   • 5 hero tiles (revenue · active orders · pending overrides · anomalies · at-risk users)
 *   • Cmd+K global search (users · orders · invoices · claims · overrides)
 *   • Live activity feed (last 50 events across the platform · auto-refreshes)
 *   • Integration health grid (12+ wired vendors)
 *   • Quick-action stats (most-used overrides · last 7d)
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  TrendingUp, Package, ShieldAlert, AlertTriangle, UserMinus,
  Activity, Search, Wifi, Loader2, ArrowUpRight, ArrowDownRight,
  Sparkles, ScrollText, LogIn, FileText, ShieldCheck, RefreshCw,
  Command, X, ChevronRight, ReceiptText,
} from "lucide-react";
import ClaimPlatformGate from "../../components/ClaimPlatformGate";


const REFRESH_MS = 30000;     // auto-refresh interval


export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/home");
      setData(r.data);
      setLastUpdate(new Date());
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Cmd+K hotkey
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
      </div>
    );
  }

  const h = data.hero_tiles || {};

  return (
    <div className="p-6 space-y-5" data-testid="admin-command-center">
      <ClaimPlatformGate/>
      <PageHeader
        title="verqo Command Center"
        subtitle={`Every signal across all portals · auto-refreshes every 30s${lastUpdate ? ` · last update ${lastUpdate.toLocaleTimeString()}` : ""}`}
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => setSearchOpen(true)}
              data-testid="cmdk-trigger"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-background hover:bg-foreground/5 text-[12px] text-foreground/65">
              <Search className="w-3.5 h-3.5"/>
              <span>Search anything</span>
              <kbd className="ml-2 mono text-[10px] px-1.5 py-0.5 rounded bg-foreground/5 border">⌘K</kbd>
            </button>
            <Button variant="ghost" size="sm" onClick={refresh} data-testid="refresh-home">
              <RefreshCw className="w-3.5 h-3.5"/>
            </Button>
          </div>
        }
      />

      {/* Hero tile row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="hero-tiles">
        <HeroTile icon={TrendingUp} label="Revenue · today"
          value={money(h.revenue_today?.fee_total || 0)}
          delta={h.revenue_today?.vs_yesterday}
          sub={`${h.revenue_today?.tx_count || 0} txns · gross ${money(h.revenue_today?.gross_total || 0)}`}
          tone="ink"
          href="/admin/revenue"
          testid="hero-revenue"/>
        <HeroTile icon={Package} label="Active orders"
          value={h.active_orders?.count ?? 0}
          sub={`${h.active_orders?.today ?? 0} created today`}
          href="/admin/commerce"
          testid="hero-orders"/>
        <HeroTile icon={ShieldAlert} label="Pending overrides"
          value={h.pending_overrides?.count ?? 0}
          sub={`${money(h.pending_overrides?.impact_usd || 0)} pending impact`}
          tone={(h.pending_overrides?.count || 0) > 0 ? "amber" : "neutral"}
          href="/admin/overrides"
          testid="hero-pending"/>
        <HeroTile icon={AlertTriangle} label="Anomalies"
          value={h.anomalies?.count ?? 0}
          sub="velocity · impact · spam · reverts"
          tone={(h.anomalies?.count || 0) > 0 ? "rose" : "good"}
          href="/admin/overrides"
          testid="hero-anomalies"/>
        <HeroTile icon={UserMinus} label="At-risk users"
          value={h.at_risk_users?.count ?? 0}
          sub={`${h.at_risk_users?.suspended ?? 0} suspended`}
          tone={(h.at_risk_users?.count || 0) > 0 ? "amber" : "good"}
          href="/admin/users"
          testid="hero-at-risk"/>
      </div>

      {/* Live activity + Integration health (2-column) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ActivityFeed events={data.activity || []}/>
        </div>
        <div className="space-y-4">
          <IntegrationHealth integrations={data.integrations}/>
          <QuickActions stats={data.quick_actions}/>
        </div>
      </div>

      <GlobalSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)}/>
    </div>
  );
}


/* ─── Hero tile ───────────────────────────────────────────────────────── */
function HeroTile({ icon: Icon, label, value, sub, delta, tone = "neutral", href, testid }) {
  const navigate = useNavigate();
  const toneCls = {
    ink:     "bg-foreground text-background border-foreground",
    good:    "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    neutral: "bg-background border-border",
  }[tone];
  return (
    <button
      onClick={() => href && navigate(href)}
      data-testid={testid}
      className={`rounded-2xl border p-4 text-left transition-transform hover:scale-[1.01] ${toneCls}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1.5">
          <Icon className="w-3 h-3"/><span>{label}</span>
        </div>
        {href && <ChevronRight className="w-3 h-3 opacity-50"/>}
      </div>
      <div className="display text-2xl mt-1.5 tracking-tight tabular-nums">{value}</div>
      {sub && <div className="text-[10.5px] opacity-65 mt-0.5">{sub}</div>}
      {delta !== undefined && delta !== null && (
        <div className={`text-[10.5px] mt-1 inline-flex items-center gap-0.5 ${delta >= 0 ? "text-emerald-200" : "text-rose-200"}`}>
          {delta >= 0 ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownRight className="w-3 h-3"/>}
          {Math.abs(delta).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} vs yesterday
        </div>
      )}
    </button>
  );
}


/* ─── Activity feed ───────────────────────────────────────────────────── */
function ActivityFeed({ events }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    if (filter === "all") return events;
    return events.filter(e => e.kind === filter);
  }, [events, filter]);
  const FILTERS = [
    { id: "all",            label: "All" },
    { id: "admin_override", label: "Overrides" },
    { id: "order_created",  label: "Orders" },
    { id: "claim",          label: "Claims" },
    { id: "admin_login",    label: "Logins" },
  ];

  return (
    <div className="rounded-2xl border bg-background overflow-hidden" data-testid="activity-feed">
      <div className="flex items-center justify-between border-b px-4 py-2.5 bg-foreground/3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-foreground/65"/>
          <div className="text-[13px] font-semibold">Live activity</div>
          <span className="text-[10.5px] mono text-foreground/55">{events.length} events</span>
        </div>
        <div className="flex gap-1">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2 py-0.5 text-[10.5px] rounded-full border transition-colors ${
                filter === f.id ? "bg-foreground text-background border-foreground" : "bg-background border-border text-foreground/65"
              }`}
              data-testid={`activity-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="py-10 text-center text-foreground/55 text-sm">No activity in this filter.</div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((e, i) => <ActivityRow key={i} e={e}/>)}
          </ul>
        )}
      </div>
    </div>
  );
}


function ActivityRow({ e }) {
  const at = e.at ? new Date(e.at) : null;
  const time = at ? at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—";
  if (e.kind === "admin_override") {
    return (
      <li className="px-4 py-2.5 flex items-center gap-3" data-testid="activity-row">
        <ShieldCheck className={`w-3.5 h-3.5 shrink-0 ${e.status === "applied" ? "text-emerald-600" : e.status === "reverted" ? "text-rose-600" : "text-amber-600"}`}/>
        <div className="flex-1 min-w-0 text-[12px]">
          <div className="font-medium truncate">{e.action}</div>
          <div className="text-[10.5px] text-foreground/55 truncate">{e.actor} · {(e.target || "").slice(0, 8) || "—"}</div>
        </div>
        {e.impact > 0 && <span className="text-[11px] mono text-foreground/70">{money(e.impact)}</span>}
        <span className="text-[10.5px] mono text-foreground/45 whitespace-nowrap">{time}</span>
      </li>
    );
  }
  if (e.kind === "order_created") {
    return (
      <li className="px-4 py-2.5 flex items-center gap-3" data-testid="activity-row">
        <Package className="w-3.5 h-3.5 shrink-0 text-foreground/65"/>
        <div className="flex-1 min-w-0 text-[12px]">
          <div className="font-medium truncate">Order {e.code}</div>
          <div className="text-[10.5px] text-foreground/55">{e.status}</div>
        </div>
        <span className="text-[11px] mono text-foreground/70">{money(e.total)}</span>
        <span className="text-[10.5px] mono text-foreground/45 whitespace-nowrap">{time}</span>
      </li>
    );
  }
  if (e.kind === "claim") {
    return (
      <li className="px-4 py-2.5 flex items-center gap-3" data-testid="activity-row">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-600"/>
        <div className="flex-1 min-w-0 text-[12px]">
          <div className="font-medium truncate">Claim {e.code} · {e.order}</div>
          <div className="text-[10.5px] text-foreground/55">{e.status}</div>
        </div>
        <span className="text-[11px] mono text-foreground/70">{money(e.value)}</span>
        <span className="text-[10.5px] mono text-foreground/45 whitespace-nowrap">{time}</span>
      </li>
    );
  }
  if (e.kind === "admin_login") {
    return (
      <li className="px-4 py-2.5 flex items-center gap-3" data-testid="activity-row">
        <LogIn className={`w-3.5 h-3.5 shrink-0 ${e.success ? "text-emerald-600" : "text-rose-600"}`}/>
        <div className="flex-1 min-w-0 text-[12px]">
          <div className="font-medium truncate">{e.email}</div>
          <div className="text-[10.5px] text-foreground/55 truncate">{e.method} · {e.ip}</div>
        </div>
        <span className={`text-[10.5px] mono ${e.success ? "text-emerald-600" : "text-rose-600"}`}>
          {e.success ? "OK" : "FAIL"}
        </span>
        <span className="text-[10.5px] mono text-foreground/45 whitespace-nowrap">{time}</span>
      </li>
    );
  }
  return null;
}


/* ─── Integration health ──────────────────────────────────────────────── */
function IntegrationHealth({ integrations }) {
  if (!integrations) return null;
  const { rows = [], configured, total } = integrations;
  return (
    <div className="rounded-2xl border bg-background overflow-hidden" data-testid="integrations-health">
      <div className="border-b px-4 py-2.5 bg-foreground/3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wifi className="w-4 h-4 text-foreground/65"/>
          <div className="text-[13px] font-semibold">Integrations</div>
        </div>
        <span className="text-[10.5px] mono text-foreground/55">{configured} / {total} live</span>
      </div>
      <ul className="max-h-[380px] overflow-y-auto divide-y divide-border">
        {rows.map(r => (
          <li key={r.id} className="px-4 py-2 flex items-center gap-2 text-[12px]" data-testid={`integration-${r.id}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${r.configured ? "bg-emerald-500" : "bg-foreground/20"}`}/>
            <div className="flex-1 min-w-0">
              <div className="truncate">{r.name}</div>
              {r.env && r.env !== "production" && <div className="text-[10px] text-foreground/45 mono">{r.env}</div>}
            </div>
            <span className={`text-[10.5px] mono ${r.configured ? "text-emerald-600" : "text-foreground/45"}`}>
              {r.configured ? "live" : "off"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}


/* ─── Quick actions ───────────────────────────────────────────────────── */
function QuickActions({ stats = [] }) {
  if (stats.length === 0) return null;
  return (
    <div className="rounded-2xl border bg-background overflow-hidden" data-testid="quick-actions">
      <div className="border-b px-4 py-2.5 bg-foreground/3 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-foreground/65"/>
        <div className="text-[13px] font-semibold">Most-used overrides · 7d</div>
      </div>
      <ul className="divide-y divide-border">
        {stats.map(s => (
          <li key={s.action} className="px-4 py-2 flex items-center justify-between text-[12px]" data-testid={`quick-action-${s.action}`}>
            <span className="mono text-foreground/85">{s.action}</span>
            <span className="text-[10.5px] mono text-foreground/55">{s.count}×</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


/* ─── Global search (Cmd+K) ───────────────────────────────────────────── */

/* Inline panic dialog — reused when admin invokes ">lockdown <email>" from Cmd+K */
function CmdkLockdownDialog({ user, onClose, onApplied }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const lockedDown = !!user?.locked_down || (user?.suspended && (user?.suspension_reason || "").startsWith("lockdown"));

  const submit = async () => {
    if (reason.trim().length < 3) { return; }
    setBusy(true);
    try {
      const path = lockedDown
        ? `/admin/users/${user.id}/unlockdown`
        : `/admin/users/${user.id}/lockdown`;
      const r = await api.post(path, { reason: reason.trim() });
      const acts = r.data?.actions_taken || [];
      onApplied?.(lockedDown
        ? `Account unlocked · ${acts.length ? acts.join(", ") : "no-op"}`
        : `🚨 Locked down · ${acts.length} sub-actions executed`);
      onClose();
    } catch (e) {
      onApplied?.(e?.response?.data?.detail || e?.message || "Failed", true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cmdk-panic-dialog" aria-describedby="cmdk-panic-desc">
        <DialogHeader>
          <DialogTitle className={lockedDown ? "text-amber-700" : "text-rose-700"}>
            {lockedDown ? "Confirm unlockdown" : "🚨 Confirm composite lockdown"}
          </DialogTitle>
          <DialogDescription id="cmdk-panic-desc">
            Target: <span className="mono">{user?.email}</span> ({user?.role}).&nbsp;
            {lockedDown
              ? "Reverses lockdown · unsuspend + unfreeze cards + unpause storefront."
              : "Executes 7 sub-actions atomically · audit-logged."}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Input
            autoFocus
            placeholder={lockedDown ? "Why unlock? (min 3 chars · audit-logged)" : "Why lock down? (min 3 chars · audit-logged · surfaced to user)"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="cmdk-panic-reason"
            onKeyDown={(e) => { if (e.key === "Enter" && reason.trim().length >= 3) submit(); }}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant={lockedDown ? "default" : "destructive"}
            onClick={submit}
            disabled={busy || reason.trim().length < 3}
            data-testid="cmdk-panic-submit"
          >
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            {lockedDown ? "Confirm unlock" : "Lock down account"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function GlobalSearchDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [panicUser, setPanicUser] = useState(null);
  const navigate = useNavigate();

  const isActionMode = q.trim().startsWith(">");
  const actionQuery = isActionMode ? q.trim().slice(1).trim() : "";
  const actionParts = actionQuery.split(/\s+/);
  const actionVerb = (actionParts[0] || "").toLowerCase();
  const actionArg = actionParts.slice(1).join(" ").trim();

  // Action catalog — extensible. Each action has a verb, aliases,
  // description, and a `target` matcher that drives the search.
  const ACTIONS = useMemo(() => ([
    {
      verb: "lockdown",
      aliases: ["lock", "panic", "freeze-account"],
      target: "user",
      tone: "rose",
      label: "🚨 Lockdown user",
      hint: "Composite freeze · suspend + kick sessions + freeze cards + delete PMs + revoke covers + pause storefront",
    },
    {
      verb: "unlockdown",
      aliases: ["unlock", "unfreeze"],
      target: "user",
      tone: "emerald",
      label: "Unlock user",
      hint: "Reverses lockdown · unsuspend + unfreeze cards + unpause storefront",
    },
  ]), []);

  const matchedActions = useMemo(() => {
    if (!isActionMode) return [];
    if (!actionVerb) return ACTIONS;
    return ACTIONS.filter(a => a.verb.startsWith(actionVerb) || a.aliases.some(x => x.startsWith(actionVerb)));
  }, [isActionMode, actionVerb, ACTIONS]);

  // Debounced search — entity-mode hits /search; action-mode-with-target hits /search too (users only)
  useEffect(() => {
    if (!open) return;
    const term = isActionMode ? actionArg : q;
    if (term.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/home/search", { params: { q: term, limit: 5 } });
        setResults(r.data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, isActionMode, actionArg]);

  useEffect(() => {
    if (open) setQ("");
  }, [open]);

  const go = (path) => { onClose(); navigate(path); };

  const triggerAction = (action, user) => {
    if (action.verb === "lockdown" || action.verb === "unlockdown") {
      onClose();
      // Brief tick so the search dialog actually closes before the panic dialog opens
      setTimeout(() => setPanicUser(user), 80);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl p-0" data-testid="cmdk-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Search</DialogTitle>
            <DialogDescription>Cmd-K global search across users, orders, invoices, claims, and overrides. Type &quot;&gt;&quot; to enter action mode.</DialogDescription>
          </DialogHeader>
          <div className={`border-b px-4 py-3 flex items-center gap-2 ${isActionMode ? "bg-rose-50/40" : ""}`}>
            <Command className={`w-4 h-4 ${isActionMode ? "text-rose-700" : "text-foreground/55"}`}/>
            <Input autoFocus
              placeholder={isActionMode
                ? "Action mode · type 'lockdown <user-email>' or 'unlockdown <user-email>'"
                : "Search users · orders · invoices · claims · overrides… (type > for actions)"}
              value={q} onChange={(e) => setQ(e.target.value)}
              className="border-0 px-0 focus-visible:ring-0 text-[14px] bg-transparent"
              data-testid="cmdk-input"/>
            {isActionMode && (
              <span className="text-[9.5px] mono uppercase tracking-[0.18em] text-rose-700 px-1.5 py-0.5 bg-rose-100 border border-rose-200 rounded">
                ACTION MODE
              </span>
            )}
            {loading && <Loader2 className="w-3 h-3 animate-spin text-foreground/40"/>}
            <button onClick={onClose} className="text-foreground/45 hover:text-foreground" data-testid="cmdk-close">
              <X className="w-4 h-4"/>
            </button>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {isActionMode ? (
              <ActionResults
                actions={matchedActions}
                userResults={results?.users || []}
                hasArg={actionArg.length >= 2}
                onTrigger={triggerAction}
              />
            ) : (
              <>
                {!results && (
                  <div className="px-4 py-10 text-center text-foreground/55 text-sm">
                    <Search className="w-5 h-5 mx-auto mb-2 opacity-50"/>
                    Type at least 2 characters. Searches across all entities.
                    <div className="mt-3 text-[10.5px] text-foreground/45">
                      Tip: type <kbd className="mono px-1 py-0.5 rounded bg-foreground/5 border">&gt;</kbd> to run actions (lockdown / unlockdown).
                    </div>
                  </div>
                )}
                {results && (
                  <div className="divide-y divide-border">
                    <SearchSection title="Users" items={results.users} testid="cmdk-users"
                      renderRow={(u) => (
                        <button key={u.id} onClick={() => go("/admin/users")}
                          className="w-full text-left px-4 py-2 hover:bg-foreground/5 flex items-center gap-2 text-[12.5px]"
                          data-testid={`cmdk-user-${u.id}`}>
                          <div className="w-6 h-6 rounded-full bg-foreground/10 grid place-items-center text-[10px]">
                            {(u.name || u.email || "?")[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{u.name || u.email}</div>
                            <div className="text-[10.5px] mono text-foreground/55 truncate">{u.email} · {u.role}</div>
                          </div>
                        </button>
                      )}/>
                    <SearchSection title="Orders" items={results.orders} testid="cmdk-orders"
                      renderRow={(o) => (
                        <button key={o.id} onClick={() => go("/admin/commerce")}
                          className="w-full text-left px-4 py-2 hover:bg-foreground/5 flex items-center gap-2 text-[12.5px]"
                          data-testid={`cmdk-order-${o.id}`}>
                          <Package className="w-3.5 h-3.5 text-foreground/55"/>
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{o.code} · {o.status}</div>
                            <div className="text-[10.5px] mono text-foreground/55">{money(o.total)}</div>
                          </div>
                        </button>
                      )}/>
                    <SearchSection title="Invoices" items={results.invoices} testid="cmdk-invoices"
                      renderRow={(i) => (
                        <button key={i.id} onClick={() => go("/admin/commerce")}
                          className="w-full text-left px-4 py-2 hover:bg-foreground/5 flex items-center gap-2 text-[12.5px]"
                          data-testid={`cmdk-invoice-${i.id}`}>
                          <ReceiptText className="w-3.5 h-3.5 text-foreground/55"/>
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{i.code} · {i.status}</div>
                            <div className="text-[10.5px] mono text-foreground/55">{money(i.amount)}</div>
                          </div>
                        </button>
                      )}/>
                    <SearchSection title="Claims" items={results.claims} testid="cmdk-claims"
                      renderRow={(c) => (
                        <button key={c.id} onClick={() => go("/admin/claims")}
                          className="w-full text-left px-4 py-2 hover:bg-foreground/5 flex items-center gap-2 text-[12.5px]"
                          data-testid={`cmdk-claim-${c.id}`}>
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600"/>
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{c.code} · order {c.order_code}</div>
                            <div className="text-[10.5px] mono text-foreground/55">{c.status} · {money(c.claim_value)}</div>
                          </div>
                        </button>
                      )}/>
                    <SearchSection title="Overrides" items={results.overrides} testid="cmdk-overrides"
                      renderRow={(o) => (
                        <button key={o.id} onClick={() => go("/admin/overrides")}
                          className="w-full text-left px-4 py-2 hover:bg-foreground/5 flex items-center gap-2 text-[12.5px]"
                          data-testid={`cmdk-override-${o.id}`}>
                          <ShieldCheck className="w-3.5 h-3.5 text-foreground/65"/>
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{o.action}</div>
                            <div className="text-[10.5px] mono text-foreground/55 truncate">{o.reason}</div>
                          </div>
                        </button>
                      )}/>
                    {[results.users, results.orders, results.invoices, results.claims, results.overrides].every(a => !a?.length) && (
                      <div className="px-4 py-8 text-center text-foreground/55 text-sm">No matches across users · orders · invoices · claims · overrides.</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="border-t px-4 py-2 text-[10.5px] text-foreground/55 flex items-center gap-3">
            <kbd className="mono px-1.5 py-0.5 rounded bg-foreground/5 border">↑↓</kbd> navigate
            <kbd className="mono px-1.5 py-0.5 rounded bg-foreground/5 border">↵</kbd> open
            <kbd className="mono px-1.5 py-0.5 rounded bg-foreground/5 border">&gt;</kbd> actions
            <kbd className="mono px-1.5 py-0.5 rounded bg-foreground/5 border">esc</kbd> close
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline panic confirmation when an admin runs `>lockdown <email>` */}
      <CmdkLockdownDialog
        user={panicUser}
        onClose={() => setPanicUser(null)}
        onApplied={(msg, isError) => {
          if (isError) {
            // Lazy toast import to avoid top-level addition
            import("sonner").then(({ toast }) => toast.error(msg));
          } else {
            import("sonner").then(({ toast }) => toast.success(msg));
          }
        }}
      />
    </>
  );
}


function ActionResults({ actions, userResults, hasArg, onTrigger }) {
  if (!actions.length) {
    return (
      <div className="px-4 py-8 text-center text-foreground/55 text-sm">
        No matching action. Available: <span className="mono">lockdown</span> · <span className="mono">unlockdown</span>.
      </div>
    );
  }
  return (
    <div className="divide-y divide-border" data-testid="cmdk-actions">
      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-foreground/55">
        Actions · {actions.length}
      </div>
      <div className="px-2 py-1">
        {actions.map(a => (
          <div key={a.verb} className={`rounded-md px-3 py-2 text-[12.5px] ${a.tone === "rose" ? "bg-rose-50/40" : "bg-emerald-50/40"}`} data-testid={`cmdk-action-${a.verb}`}>
            <div className="font-medium">{a.label} <span className="text-foreground/45 font-normal">— type a user email or name to target</span></div>
            <div className="text-[10.5px] text-foreground/55">{a.hint}</div>
          </div>
        ))}
      </div>
      {hasArg && (
        <div data-testid="cmdk-action-targets">
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-foreground/55">
            Targets · {userResults.length}
          </div>
          {userResults.length === 0 ? (
            <div className="px-4 py-3 text-foreground/55 text-[12.5px]">No matching users.</div>
          ) : userResults.map(u => actions.map(a => (
            <button
              key={`${a.verb}-${u.id}`}
              onClick={() => onTrigger(a, u)}
              className="w-full text-left px-4 py-2 hover:bg-rose-50/60 flex items-center gap-2 text-[12.5px]"
              data-testid={`cmdk-action-target-${a.verb}-${u.id}`}
            >
              <span className={`text-[9.5px] mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border ${
                a.tone === "rose" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"
              }`}>
                {a.verb}
              </span>
              <div className="flex-1 min-w-0">
                <div className="truncate">{u.name || u.email}</div>
                <div className="text-[10.5px] mono text-foreground/55 truncate">{u.email} · {u.role}</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-foreground/35"/>
            </button>
          )))}
        </div>
      )}
    </div>
  );
}


function SearchSection({ title, items, renderRow, testid }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testid}>
      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-foreground/55">{title} · {items.length}</div>
      {items.map(renderRow)}
    </div>
  );
}
